import type { ControlInput, GameSnapshot, RoomState } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import { clamp, createCard } from '../controllerUiHelpers.js';
import { PeriscopeCameraView } from '../rendering/PeriscopeCameraView.js';
import type { GraphicsQuality } from '../rendering/quality.js';
import type { RenderTuning } from '../rendering/renderTuning.js';

type CreatePeriscopeRoomControllerOptions = {
  controls: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
  getGraphicsQuality: () => GraphicsQuality;
  getRenderTuning: () => RenderTuning;
  onSnapshotPredicted: (snapshot: GameSnapshot) => void;
};

const PERISCOPE_YAW_DEG_PER_PX = 0.35;
const PERISCOPE_PITCH_DEG_PER_PX = 0.3;

export const createPeriscopeRoomController = ({
  controls,
  emitControl,
  getGraphicsQuality,
  getRenderTuning,
  onSnapshotPredicted
}: CreatePeriscopeRoomControllerOptions) => {
  let periscopeCameraView: PeriscopeCameraView | null = null;
  let periscopeAimState = { yaw: 0, pitch: 0 };
  let periscopeLightsButton: HTMLButtonElement | null = null;
  let periscopeOrientationThrottleId: number | null = null;

  const setRenderTuning = (value: RenderTuning): void => {
    periscopeCameraView?.setRenderTuning(value);
  };

  const setSnapshot = (snapshot: GameSnapshot): void => {
    periscopeCameraView?.setSnapshot(snapshot);
  };

  const setRoomState = (state: RoomState): void => {
    periscopeCameraView?.setRoomState(state);
  };

  const createLightsButton = (): HTMLButtonElement => {
    const lightsToggle = document.createElement('button');
    lightsToggle.type = 'button';
    lightsToggle.className = 'primary';
    lightsToggle.textContent = controllerState.lastSnapshot?.submarine.periscopeLightOn
      ? 'Turn periscope\nlight OFF'
      : 'Turn periscope\nlight ON';
    periscopeLightsButton = lightsToggle;

    lightsToggle.addEventListener('click', () => {
      if (!controllerState.currentRoomCode || !controllerState.lastSnapshot) {
        return;
      }
      const nextOn = !controllerState.lastSnapshot.submarine.periscopeLightOn;
      const predictedSnapshot = {
        ...controllerState.lastSnapshot,
        submarine: {
          ...controllerState.lastSnapshot.submarine,
          periscopeLightOn: nextOn
        }
      };
      controllerState.lastSnapshot = predictedSnapshot;
      emitControl({ action: 'toggle_periscope_light', active: nextOn });
      periscopeCameraView?.setSnapshot(predictedSnapshot);
      onSnapshotPredicted(predictedSnapshot);
      // Update via local reference (robust even if periscopeLightsButton was reassigned)
      lightsToggle.textContent = nextOn ? 'Turn periscope\nlight OFF' : 'Turn periscope\nlight ON';
      periscopeLightsButton = lightsToggle; // ensure module var stays in sync
    });

    return lightsToggle;
  };

  const render = (): void => {
    if (!controls) {
      return;
    }

    if (controllerState.lastSnapshot) {
      periscopeAimState = {
        yaw: controllerState.lastSnapshot.submarine.periscopeYaw,
        pitch: controllerState.lastSnapshot.submarine.periscopePitch
      };
    }

    const cameraCard = createCard('Periscope camera', 'Conning-tower mounted search camera. Drag to rotate 360 degrees and tilt up or down.');
    cameraCard.classList.add('ctrl-room-main-card');
    controls.classList.add('camera-role-layout');
    const cameraViewport = document.createElement('div');
    cameraViewport.className = 'periscope-camera-viewport role-camera-viewport';

    const reticle = document.createElement('div');
    reticle.style.position = 'absolute';
    reticle.style.left = '50%';
    reticle.style.top = '50%';
    reticle.style.width = '30px';
    reticle.style.height = '30px';
    reticle.style.pointerEvents = 'none';
    reticle.style.transform = 'translate(-50%, -50%)';
    reticle.style.boxSizing = 'border-box';
    reticle.style.border = '1px solid rgba(255, 255, 255, 0.85)';
    reticle.style.borderRadius = '50%';
    reticle.style.boxShadow = '0 0 10px rgba(255, 255, 255, 0.35)';
    reticle.style.zIndex = '1';
    reticle.innerHTML = '<div style="position:absolute;left:50%;top:0;width:2px;height:100%;background:rgba(255,255,255,0.85);transform:translateX(-50%);"></div><div style="position:absolute;top:50%;left:0;width:100%;height:2px;background:rgba(255,255,255,0.85);transform:translateY(-50%);"></div>';
    cameraViewport.append(reticle);

    cameraCard.append(cameraViewport);
    controls.append(cameraCard);

    periscopeCameraView?.dispose();
    periscopeCameraView = new PeriscopeCameraView(cameraViewport, getGraphicsQuality(), getRenderTuning());
    if (controllerState.lastRoomState) {
      periscopeCameraView.setRoomState(controllerState.lastRoomState);
    }
    if (controllerState.lastSnapshot) {
      periscopeCameraView.setSnapshot(controllerState.lastSnapshot);
    }
    periscopeCameraView.setYawPitch(periscopeAimState.yaw, periscopeAimState.pitch);

    const emitOrientationThrottled = () => {
      if (periscopeOrientationThrottleId !== null) {
        return;
      }
      periscopeOrientationThrottleId = window.setTimeout(() => {
        periscopeOrientationThrottleId = null;
        emitControl({
          action: 'set_periscope_orientation',
          x: periscopeAimState.yaw,
          y: periscopeAimState.pitch
        });
      }, 100);
    };

    let dragPointerX = 0;
    let dragPointerY = 0;
    let aimDragging = false;

    cameraViewport.style.touchAction = 'none';

    cameraViewport.addEventListener('pointerdown', (event) => {
      aimDragging = true;
      dragPointerX = event.clientX;
      dragPointerY = event.clientY;
      cameraViewport.setPointerCapture(event.pointerId);
    });

    cameraViewport.addEventListener('pointermove', (event) => {
      if (!aimDragging) {
        return;
      }
      const dx = event.clientX - dragPointerX;
      const dy = event.clientY - dragPointerY;
      dragPointerX = event.clientX;
      dragPointerY = event.clientY;

      periscopeAimState.yaw -= dx * PERISCOPE_YAW_DEG_PER_PX;
      periscopeAimState.pitch -= dy * PERISCOPE_PITCH_DEG_PER_PX;
      periscopeAimState.pitch = clamp(periscopeAimState.pitch, -60, 90);

      periscopeCameraView?.setYawPitch(periscopeAimState.yaw, periscopeAimState.pitch);
      emitOrientationThrottled();
    });

    const stopDrag = (): void => {
      aimDragging = false;
    };
    cameraViewport.addEventListener('pointerup', stopDrag);
    cameraViewport.addEventListener('pointercancel', stopDrag);
  };

  const updateLightsButton = (periscopeLightOn: boolean): void => {
    if (periscopeLightsButton) {
      periscopeLightsButton.textContent = periscopeLightOn
        ? 'Turn periscope\nlight OFF'
        : 'Turn periscope\nlight ON';
    }
  };

  return {
    render,
    createLightsButton,
    updateLightsButton,
    setRenderTuning,
    setSnapshot,
    setRoomState
  };
};
